import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CalculatorIcon,
  CogIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
  NoSymbolIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthProvider';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.ComponentProps<'svg'>>;
  siteAdminOnly?: boolean;
  childLabel?: string;
  children?: {
    name: string;
    href: string;
    icon?: React.ComponentType<React.ComponentProps<'svg'>>;
    siteAdminOnly?: boolean;
  }[];
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Orgs', href: '/orgs', icon: BuildingOfficeIcon },
  {
    name: 'Signup Queue',
    href: '/signup-queue',
    icon: ClipboardDocumentListIcon,
    siteAdminOnly: true,
    children: [{ name: 'Blocklist', href: '/signup-queue/blocklist', icon: NoSymbolIcon }],
  },
  {
    name: 'Catalog',
    href: '/catalog/plans',
    icon: CurrencyDollarIcon,
    siteAdminOnly: true,
    children: [
      { name: 'Features', href: '/catalog/features', icon: CogIcon },
      { name: 'Calculator', href: '/catalog/calculator', icon: CalculatorIcon },
    ],
  },
  {
    name: 'Access',
    href: '/access/users',
    icon: UserGroupIcon,
    siteAdminOnly: true,
    childLabel: 'Admin',
    children: [{ name: 'Partners', href: '/access/partners', icon: UserGroupIcon }],
  },
  {
    name: 'Operations',
    href: '/ops/status',
    icon: WrenchScrewdriverIcon,
    children: [
      { name: 'Sync & Cleanup', href: '/ops/sync', icon: ArrowPathIcon },
      { name: 'Audit Logs', href: '/ops/logs', icon: DocumentTextIcon },
    ],
  },
  { name: 'Settings', href: '/settings', icon: AdjustmentsHorizontalIcon },
];

function filterNav(items: NavItem[], isSiteAdmin: boolean): NavItem[] {
  return items
    .filter((item) => !item.siteAdminOnly || isSiteAdmin)
    .map((item) => {
      if (!item.children) return item;
      const filtered = item.children.filter((c) => !c.siteAdminOnly || isSiteAdmin);
      return { ...item, children: filtered.length > 0 ? filtered : undefined };
    });
}

const STORAGE_KEY = 'sidebar_collapsed';

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { isSiteAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const onMobileCloseRef = useRef(onMobileClose);
  onMobileCloseRef.current = onMobileClose;

  const visibleNav = filterNav(navigation, isSiteAdmin);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Close mobile sidebar on navigation
  useEffect(() => {
    onMobileCloseRef.current();
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;
  const isGroupActive = (item: NavItem) =>
    isActive(item.href) || item.children?.some((c) => isActive(c.href));

  const toggleGroup = (name: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const sidebarWidth = collapsed ? 'w-16' : 'w-[250px]';

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = isGroupActive(item);
    const hasChildren = item.children && item.children.length > 0;
    const expanded = expandedGroups.has(item.name) || (hasChildren && active);

    return (
      <li key={item.name}>
        {hasChildren ? (
          <>
            <button
              onClick={() => {
                if (collapsed) {
                  setCollapsed(false);
                  setExpandedGroups((prev) => new Set(prev).add(item.name));
                } else {
                  toggleGroup(item.name);
                }
              }}
              className={`flex items-center w-full h-12 px-3 gap-3 text-xs uppercase tracking-wider font-medium transition-colors rounded-lg ${
                active
                  ? 'bg-primary text-primary-content'
                  : 'text-base-content/70 hover:bg-base-300/50 hover:text-base-content'
              }`}
              title={collapsed ? item.name : undefined}
            >
              <Icon className="h-5 w-5 shrink-0" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{item.name}</span>
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </button>
            {!collapsed && expanded && (
              <ul className="ml-4 mt-1 space-y-0.5">
                <li>
                  <Link
                    to={item.href}
                    className={`flex items-center h-10 px-3 gap-3 text-xs uppercase tracking-wider rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary/20 text-primary font-semibold'
                        : 'text-base-content/60 hover:bg-base-300/50 hover:text-base-content'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.childLabel || item.name}</span>
                  </Link>
                </li>
                {item.children!.map((child) => {
                  const ChildIcon = child.icon;
                  return (
                    <li key={child.name}>
                      <Link
                        to={child.href}
                        className={`flex items-center h-10 px-3 gap-3 text-xs uppercase tracking-wider rounded-lg transition-colors ${
                          isActive(child.href)
                            ? 'bg-primary/20 text-primary font-semibold'
                            : 'text-base-content/60 hover:bg-base-300/50 hover:text-base-content'
                        }`}
                      >
                        {ChildIcon && <ChildIcon className="h-4 w-4 shrink-0" />}
                        <span>{child.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <Link
            to={item.href}
            className={`flex items-center h-12 px-3 gap-3 text-xs uppercase tracking-wider font-medium transition-colors rounded-lg ${
              active
                ? 'bg-primary text-primary-content'
                : 'text-base-content/70 hover:bg-base-300/50 hover:text-base-content'
            }`}
            title={collapsed ? item.name : undefined}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!collapsed && <span>{item.name}</span>}
          </Link>
        )}
      </li>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5">{visibleNav.map(renderItem)}</ul>
      </nav>

      {/* Collapse toggle (desktop only) */}
      <div className="hidden lg:flex items-center justify-end p-2 border-t border-base-300">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="btn btn-ghost btn-sm btn-square"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRightIcon className="h-4 w-4" />
          ) : (
            <ChevronLeftIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={onMobileClose} />
      )}

      {/* Mobile sidebar (drawer) */}
      <aside
        className={`fixed top-[60px] bottom-0 left-0 z-40 w-[250px] bg-base-100 border-r border-base-300 transform transition-transform duration-300 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:block fixed top-[60px] bottom-0 left-0 z-20 ${sidebarWidth} bg-base-100 border-r border-base-300 transition-all duration-300`}
      >
        {sidebarContent}
      </aside>

      {/* Spacer to push main content right (desktop only) */}
      <div className={`hidden lg:block shrink-0 ${sidebarWidth} transition-all duration-300`} />
    </>
  );
};
